import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, FileText, Plus } from "@/lib/icons";
import { useState } from "react";
import { toast } from "sonner";
import {
  formatDateOnly,
  ORDER_TYPES,
  SELF_MED_APPROVAL_LABEL,
  type SelfMedApproval,
  errorWords,
} from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { RowActions } from "@/components/app/patterns/row-actions";
import { DataTable } from "@/components/app/patterns/data-table";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { OrderDetailSheet } from "@/components/app/orders/order-detail-sheet";
import { OrderEditorDialog } from "@/components/app/orders/order-editor-dialog";
import { orderKindRowClass } from "@/components/app/orders/order-kind";
import {
  ORDER_TYPE_LABELS,
  orderNeedsAttention,
  type Order,
  type OrderType,
} from "@/components/app/orders/order-utils";
import { SupplyDialog } from "@/components/app/orders/supply-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { saveFile } from "@/lib/files/download";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { optionalTrue, KEEP_PLACE } from "@/lib/search";
import { useZone } from "@/lib/facility/zone";
import { orderColumns } from "./-components/documents/order-columns";
import { EmergencyPacketButton } from "./-components/documents/emergency-packet";
import { SuspendAllOrdersButton } from "./-components/documents/suspend-all-orders";

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/medications")({
  validateSearch: (search: Record<string, unknown>): { ended?: true } => ({
    ended: optionalTrue(search.ended),
  }),
  component: OrdersTab,
});

type Editor = { type: OrderType; order: Order | null; copyFrom: Order | null } | null;
const byName = (a: Order, b: Order) =>
  Number(b.pinned) - Number(a.pinned) || a.name.localeCompare(b.name);

/** Medication, treatment, vital sign, and informational orders. */
function OrdersTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const zone = useZone(resident.facilityId);
  const canManage = useCan("medication:manage", resident.facilityId);
  // Starting and holding orders is anyone's on the pass.
  const canOrder = useCan("medication:administer", resident.facilityId);
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const showDiscontinued = Boolean(Route.useSearch().ended);
  const setShowDiscontinued = (next: boolean) =>
    void navigate({
      search: { ended: next ? true : undefined },
      ...KEEP_PLACE,
    });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [supplyId, setSupplyId] = useState<string | null>(null);
  const [editor, setEditor] = useState<Editor>(null);
  const orders = useQuery(
    orpc.care.residents.orders.list.queryOptions({
      input: { residentId, includeDiscontinued: true },
    }),
  );
  const selfMed = useQuery(
    orpc.care.selfMedAssessments.latest.queryOptions({ input: { residentId } }),
  );
  const orderSheet = useMutation(
    orpc.care.residents.orders.sheetPdf.mutationOptions({
      onSuccess: saveFile,
      onError: (error) => toast.error(errorWords(error)),
    }),
  );
  const setSelfMedPass = useMutation(
    orpc.care.residents.update.mutationOptions({
      onSuccess: async (_data, variables) => {
        toast.success(variables.selfMedPass ? "Self med pass on" : "Self med pass off", {
          description: variables.selfMedPass
            ? "Orders stay current here; the resident is off the med-pass roster and refill queue."
            : "The resident is back on the med-pass roster and refill queue.",
        });
        await queryClient.invalidateQueries({ queryKey: orpc.care.residents.key() });
        await queryClient.invalidateQueries({ queryKey: orpc.pharmacy.medPass.key() });
      },
      onError: (error) => toast.error(`Could not update: ${errorWords(error)}`),
    }),
  );

  const all = orders.data ?? [];
  const active = all.filter((order) => order.status !== "discontinued").toSorted(byName);
  const discontinued = all.filter((order) => order.status === "discontinued").toSorted(byName);
  const needsReview = active.filter(orderNeedsAttention).length;
  const selected = all.find((order) => order.id === selectedId) ?? null;
  const supplyOrder = all.find((order) => order.id === supplyId) ?? null;
  const create = (type: OrderType) => setEditor({ type, order: null, copyFrom: null });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Orders</h2>
          <p className="text-muted-foreground text-sm">
            Medication, treatment, vital sign, and informational orders for {resident.firstName}{" "}
            {resident.lastName}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {resident.selfMedPass ? (
            <Badge variant="info">Self med pass, resident administers own meds</Badge>
          ) : null}
          {selfMed.data ? (
            <Link
              to="/residents/$residentId/plans"
              params={{ residentId }}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-xs"
              title="Open the Care plans tab"
            >
              {selfMed.data.approval ? (
                <Badge variant="outline">
                  {SELF_MED_APPROVAL_LABEL[selfMed.data.approval as SelfMedApproval]}
                </Badge>
              ) : null}
              <span>assessed {formatDateOnly(selfMed.data.assessmentDate)}</span>
            </Link>
          ) : null}
          {canManage ? (
            <div className="flex items-center gap-2">
              <Switch
                id="self-med-pass"
                checked={resident.selfMedPass}
                disabled={setSelfMedPass.isPending}
                onCheckedChange={(next) => setSelfMedPass.mutate({ residentId, selfMedPass: next })}
              />
              <Label htmlFor="self-med-pass" className="cursor-pointer text-sm">
                Self med pass
              </Label>
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <Switch
              id="show-discontinued"
              checked={showDiscontinued}
              onCheckedChange={setShowDiscontinued}
            />
            <Label htmlFor="show-discontinued" className="cursor-pointer text-sm">
              Show discontinued
            </Label>
          </div>
          <EmergencyPacketButton residentId={residentId} />
          {canOrder ? <SuspendAllOrdersButton residentId={residentId} /> : null}
          <Button
            size="sm"
            variant="outline"
            disabled={orderSheet.isPending}
            onClick={() => orderSheet.mutate({ residentId })}
          >
            <FileText className="size-4" />
            {orderSheet.isPending ? "Preparing…" : "Order sheet"}
          </Button>
          {canOrder ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm">
                  <Plus className="size-4" />
                  Add order
                  <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {ORDER_TYPES.map((type) => (
                  <DropdownMenuItem key={type} onSelect={() => create(type)}>
                    {ORDER_TYPE_LABELS[type]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      {needsReview > 0 ? (
        <div className="border-warn-border bg-warn-soft text-warn flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <AlertTriangle className="size-4 shrink-0" />
          {needsReview} order{needsReview === 1 ? "" : "s"} need{needsReview === 1 ? "s" : ""}{" "}
          review
        </div>
      ) : null}

      {orders.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : orders.isError ? (
        <ReadFailed
          message="Could not load this resident's orders."
          error={orders.error}
          onRetry={() => void orders.refetch()}
          retrying={orders.isFetching}
        />
      ) : active.length === 0 && (!showDiscontinued || discontinued.length === 0) ? (
        <Card className="py-0">
          <EmptyState
            title="No orders yet"
            description="Add medication, treatment, vital sign, or informational orders to build this resident's MAR."
          />
        </Card>
      ) : (
        <DataTable
          quiet
          data={showDiscontinued ? [...active, ...discontinued] : active}
          columns={orderColumns(zone)}
          noun="orders"
          rowId={(order) => order.id}
          onRowClick={(order) => setSelectedId(order.id)}
          rowClassName={orderKindRowClass}
          renderRowActions={(order) =>
            order.orderType === "medication" && order.status !== "discontinued" ? (
              <RowActions
                label={order.name}
                modal={false}
                items={[{ label: "Supply…", onSelect: () => setSupplyId(order.id) }]}
              />
            ) : null
          }
        />
      )}

      {selected ? (
        <OrderDetailSheet
          order={selected}
          residentId={residentId}
          facilityId={resident.facilityId}
          timezone={zone}
          onClose={() => setSelectedId(null)}
          onEdit={(order) =>
            setEditor({ type: order.orderType as OrderType, order, copyFrom: null })
          }
          onDuplicate={(order, variant) => {
            const copy =
              variant === "different"
                ? {
                    ...order,
                    name: "",
                    strength: "",
                    equivalentTo: null,
                    instructions: "",
                    instructionsOverridden: false,
                    isControlled: false,
                    controlledSchedule: null,
                    slidingScale: null,
                  }
                : order;
            setSelectedId(null);
            setEditor({ type: order.orderType as OrderType, order: null, copyFrom: copy });
          }}
        />
      ) : null}
      {supplyOrder ? <SupplyDialog order={supplyOrder} onClose={() => setSupplyId(null)} /> : null}
      {editor ? (
        <OrderEditorDialog
          residentId={residentId}
          facilityId={resident.facilityId}
          timezone={zone}
          orderType={editor.type}
          order={editor.order}
          copyFrom={editor.copyFrom}
          onClose={() => setEditor(null)}
        />
      ) : null}
    </div>
  );
}
